/**
 * Purchasing Tabs Settings — toggle sub-tabs in the Purchasing page.
 * Clones the DashboardTabsTab pattern (CR-4441).
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { ShoppingCart } from 'lucide-react';
import type { PurchasingTabsVisibility } from '../../../lib/api/types/common';

const PURCHASING_TAB_REGISTRY: { id: string; label: string; description: string; defaultOn?: boolean }[] = [
  { id: 'rfq', label: 'RFQ', description: 'Request for Quotation management' },
  { id: 'pricing', label: 'Pricebooks', description: 'Contract pricing and price books' },
  { id: 'po_headers', label: 'PO Headers', description: 'Purchase order header listing' },
  { id: 'po_lines', label: 'PO Lines', description: 'Purchase order line items with actions' },
  { id: 'forecast', label: 'Forecast', description: 'Demand forecasting and planning' },
  // CR-8623: off by default, so switching it on is a deliberate choice per install.
  { id: 'late_to_raise', label: 'Late to Raise', description: 'Forecast orders past their order-by date, with order history (customers and Helix staff; never suppliers)', defaultOn: false },
  { id: 'asn', label: 'ASN', description: 'Advanced Shipping Notices' },
  { id: 'deliveries', label: 'Deliveries', description: 'Goods Receipt Notes and delivery tracking' },
  { id: 'call_off', label: 'Call Off', description: 'Call-off orders and stock management' },
];

interface PurchasingTabsTabProps {
  purchasingTabs: PurchasingTabsVisibility;
  saving: boolean;
  onPurchasingTabsChange: (tabs: PurchasingTabsVisibility) => void;
  onSave: () => void;
}

export function PurchasingTabsTab({ purchasingTabs, saving, onPurchasingTabsChange, onSave }: PurchasingTabsTabProps) {
  const handleToggle = (tabId: string, enabled: boolean) => {
    onPurchasingTabsChange({ ...purchasingTabs, [tabId]: enabled });
  };

  const isOn = (tab: { id: string; defaultOn?: boolean }) => purchasingTabs[tab.id] ?? (tab.defaultOn ?? true);
  const enabledCount = PURCHASING_TAB_REGISTRY.filter(isOn).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Purchasing Tab Visibility
          </CardTitle>
          <CardDescription>
            Choose which tabs are visible on the Purchasing page. Disabled tabs will be hidden.
          </CardDescription>
          <span className="text-sm text-muted-foreground pt-2">
            {enabledCount} of {PURCHASING_TAB_REGISTRY.length} tabs enabled
          </span>
        </CardHeader>
        <CardContent className="space-y-3">
          {PURCHASING_TAB_REGISTRY.map((tab) => (
            <div key={tab.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
              <div>
                <Label className="text-base font-medium">{tab.label}</Label>
                <p className="text-sm text-muted-foreground">{tab.description}</p>
              </div>
              <Switch
                checked={isOn(tab)}
                onCheckedChange={(checked) => handleToggle(tab.id, checked)}
              />
            </div>
          ))}
          <Button onClick={onSave} disabled={saving} className="w-full mt-4">
            {saving ? 'Saving...' : 'Save Purchasing Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
